import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Bot, Search, Edit, Trash2, X, CheckSquare, Square, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Upload, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './Requests.css';

// Импортируем данные городов
import citiesData from '../data/cities_simple.json';

const Requests = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('google');
  const [requests, setRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]); // Все запросы для подсчета
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem('selectedProject');
    return (saved === 'unassigned') ? 'all' : (saved || 'all');
  }); // 'all' или id проекта
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showBulkCheckConfirm, setShowBulkCheckConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkChecking, setIsBulkChecking] = useState(false);
  const { user } = useAuth();

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Состояние для ИИ-генерации
  const [aiFormData, setAiFormData] = useState({
    projectId: null,
    prompt: '',
    city: 'moscow',
    searchEngines: ['google', 'yandex']
  });
  const [generatedRequests, setGeneratedRequests] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSavingBulk, setIsSavingBulk] = useState(false);
  const [selectedAiCityName, setSelectedAiCityName] = useState('Москва');
  const [aiCitySearch, setAiCitySearch] = useState('Москва');
  const [showAiCityDropdown, setShowAiCityDropdown] = useState(false);

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importFormData, setImportFormData] = useState({
    projectId: null,
    city: 'moscow',
    searchEngines: ['google', 'yandex']
  });
  const [importCitySearch, setImportCitySearch] = useState('Москва');
  const [selectedImportCityName, setSelectedImportCityName] = useState('Москва');
  const [showImportCityDropdown, setShowImportCityDropdown] = useState(false);

  const [formData, setFormData] = useState({
    query: '',
    city: 'moscow', // По умолчанию Москва
    searchEngines: ['google', 'yandex'], // По умолчанию обе поисковые системы
    projectId: null // Добавляем поле для выбора проекта
  });

  // Состояние для поиска городов
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [selectedCityName, setSelectedCityName] = useState('Москва');

  // Преобразуем данные городов в массив для удобства работы
  const cities = Object.entries(citiesData).map(([key, value]) => ({
    key,
    name: value
  }));

  // Фильтрация городов по поиску
  const filteredCities = cities.filter(city =>
    city.name.toLowerCase().includes(citySearch.toLowerCase())
  );

  // Фильтрация городов для ИИ поиска
  const aiFilteredCities = cities.filter(city =>
    city.name.toLowerCase().includes(aiCitySearch.toLowerCase())
  );

  // Обработка выбора города
  const handleCitySelect = (cityKey, cityName) => {
    setFormData({ ...formData, city: cityKey });
    setSelectedCityName(cityName);
    setCitySearch(cityName);
    setShowCityDropdown(false);
  };

  // Обработка изменения выбора поисковых систем
  const handleSearchEngineChange = (engine) => {
    const currentEngines = formData.searchEngines;
    let newEngines;

    if (currentEngines.includes(engine)) {
      // Если уже выбрана, убираем её (но не даем убрать все)
      newEngines = currentEngines.filter(e => e !== engine);
      if (newEngines.length === 0) {
        return; // Не даем убрать все поисковые системы
      }
    } else {
      // Если не выбрана, добавляем её
      newEngines = [...currentEngines, engine];
    }

    setFormData({ ...formData, searchEngines: newEngines });
  };

  // Получение проектов с сервера
  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Получение всех запросов для подсчета
  const fetchAllRequests = async () => {
    try {
      // Загружаем все запросы без фильтров для правильного подсчета
      const response = await axios.get('/api/requests');
      setAllRequests(response.data);
    } catch (error) {
      console.error('Error fetching all requests:', error);
    }
  };

  // Получение запросов с сервера
  const fetchRequests = async () => {
    try {
      setLoading(true);
      let url = `/api/requests?search_engine=${activeTab}`;

      // Добавляем фильтр по проекту
      if (selectedProject !== 'all') {
        url += `&project_id=${selectedProject}`;
      }

      const response = await axios.get(url);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchProjects();
      fetchAllRequests();
      fetchRequests();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchAllRequests(); // Обновляем все запросы при изменении проекта
      fetchRequests(); // Обновляем отфильтрованные запросы
    }
  }, [activeTab, selectedProject]);

  // Сброс страницы и выделения при изменении таба или проекта
  useEffect(() => {
    setCurrentPage(1);
    setSelectedForDelete(new Set()); // Сбрасываем выделение при смене таба/проекта
  }, [activeTab, selectedProject]);

  // Закрытие выпадающего списка городов при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.city-search-container')) {
        setShowCityDropdown(false);
        setShowAiCityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Пагинация
  const totalPages = Math.ceil(requests.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = indexOfFirstItem + itemsPerPage;

  const getCurrentRequests = useMemo(() => {
    return requests.slice(indexOfFirstItem, indexOfLastItem);
  }, [requests, indexOfFirstItem, indexOfLastItem]);

  // Обработка смены страницы
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Обработка смены количества элементов на странице
  const handleItemsPerPageChange = useCallback((event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  }, []);

  // Функция для подсчета запросов по поисковой системе с учетом выбранного проекта
  const getCountForEngine = (engine) => {
    // Исключаем запросы без проекта
    let filteredRequests = allRequests.filter(r =>
      r.search_engine === engine && r.project_id !== null
    );

    if (selectedProject !== 'all') {
      // Показать запросы конкретного проекта
      filteredRequests = filteredRequests.filter(r => {
        return r.project_id?.toString() === selectedProject;
      });
    }

    return filteredRequests.length;
  };

  // Все запросы для выбранного проекта (и Google, и Yandex) - для функции "Выбрать все"
  const allProjectRequests = useMemo(() => {
    // Исключаем запросы без проекта
    let filteredRequests = allRequests.filter(r => r.project_id !== null);

    if (selectedProject !== 'all') {
      // Показать запросы конкретного проекта
      filteredRequests = filteredRequests.filter(r => {
        return r.project_id?.toString() === selectedProject;
      });
    }

    return filteredRequests;
  }, [allRequests, selectedProject]);



  const handleAddRequest = () => {
    // Проверяем, есть ли проекты
    if (projects.length === 0) {
      alert('Сначала создайте проект для группировки запросов!');
      return;
    }

    // Определяем проект для нового запроса
    let defaultProjectId;
    if (selectedProject === 'all') {
      // Если выбраны все проекты, берем первый проект
      defaultProjectId = projects[0]?.id;
    } else {
      // Если выбран конкретный проект, используем его
      defaultProjectId = selectedProject;
    }

    setEditingRequest(null);
    setFormData({
      query: '',
      city: 'moscow',
      searchEngines: ['google', 'yandex'], // По умолчанию обе
      projectId: defaultProjectId
    });
    setCitySearch('Москва');
    setSelectedCityName('Москва');
    setShowCityDropdown(false);
    setShowModal(true);
  };

  const handleAddWithAI = () => {
    // Проверяем, есть ли проекты
    if (projects.length === 0) {
      alert('Сначала создайте проект для группировки запросов!');
      return;
    }

    // Определяем проект для генерации
    let defaultProjectId;
    if (selectedProject === 'all') {
      defaultProjectId = projects[0]?.id;
    } else {
      defaultProjectId = selectedProject;
    }

    setAiFormData({
      projectId: defaultProjectId,
      prompt: '',
      city: 'moscow',
      searchEngines: ['google', 'yandex']
    });
    setGeneratedRequests([]);
    setSelectedRequests(new Set());
    setAiCitySearch('Москва');
    setSelectedAiCityName('Москва');
    setShowAiCityDropdown(false);
    setShowAIModal(true);
  };

  const handleEdit = (request) => {
    setEditingRequest(request);
    // Найдем ключ города по названию региона
    const cityKey = Object.entries(citiesData).find(([key, value]) => value === request.region)?.[0] || 'moscow';
    const cityName = citiesData[cityKey] || 'Москва';
    setFormData({
      query: request.query,
      city: cityKey,
      searchEngines: [request.search_engine], // При редактировании только текущая поисковая система
      projectId: request.project_id || null
    });
    setCitySearch(cityName);
    setSelectedCityName(cityName);
    setShowCityDropdown(false);
    setShowModal(true);
  };

  const handleDelete = (id, e) => {
    // Предотвращаем всплытие событий
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Показываем наше модальное подтверждение
    setShowDeleteConfirm(id);
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;

    try {
      setDeletingId(showDeleteConfirm);
      await axios.delete(`/api/requests/${showDeleteConfirm}`);
      await Promise.all([
        fetchAllRequests(), // Обновляем все запросы для счетчиков
        fetchRequests() // Обновляем отфильтрованный список запросов
      ]);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('Ошибка при удалении запроса');
    } finally {
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  // Функции для массового удаления
  const handleToggleSelect = (id) => {
    const newSelected = new Set(selectedForDelete);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedForDelete(newSelected);
  };

  const handleSelectAllForDelete = () => {
    // Выбираем ВСЕ запросы проекта (и Google, и Yandex), а не только текущую вкладку
    if (selectedForDelete.size === allProjectRequests.length && allProjectRequests.length > 0) {
      setSelectedForDelete(new Set());
    } else {
      setSelectedForDelete(new Set(allProjectRequests.map(r => r.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedForDelete.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedForDelete.size === 0) return;

    try {
      setIsBulkDeleting(true);
      await axios.post('/api/requests/bulk-delete', {
        ids: Array.from(selectedForDelete)
      });
      await Promise.all([
        fetchAllRequests(),
        fetchRequests()
      ]);
      setSelectedForDelete(new Set());
      setShowBulkDeleteConfirm(false);
    } catch (error) {
      console.error('Error bulk deleting requests:', error);
      alert('Ошибка при удалении запросов');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteConfirm(false);
  };

  // Функция для массовой проверки — показываем диалог подтверждения
  const handleBulkCheck = () => {
    if (selectedForDelete.size === 0) return;
    setShowBulkCheckConfirm(true);
  };

  const cancelBulkCheck = () => {
    setShowBulkCheckConfirm(false);
  };

  const confirmBulkCheck = async () => {
    if (selectedForDelete.size === 0) return;

    try {
      setIsBulkChecking(true);
      setShowBulkCheckConfirm(false);
      const response = await axios.post('/api/requests/bulk-check', {
        ids: Array.from(selectedForDelete)
      });

      if (response.data.success) {
        alert(response.data.message);
        setSelectedForDelete(new Set());
        // Редирект на страницу истории проверок
        navigate('/dashboard/history');
      }
    } catch (error) {
      console.error('Error bulk checking requests:', error);
      const errorMessage = error.response?.data?.error || 'Ошибка при запуске проверок';
      alert(errorMessage);
    } finally {
      setIsBulkChecking(false);
    }
  };

  // Состояние для проверки запросов
  const [checkingId, setCheckingId] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [showCheckResultModal, setShowCheckResultModal] = useState(false);

  const handleCheck = async (id) => {
    try {
      setCheckingId(id);

      const response = await axios.post(`/api/requests/${id}/check`);

      if (response.data.success) {
        // Редирект на страницу истории проверок
        navigate('/dashboard/history');
      }
    } catch (error) {
      console.error('Error starting check:', error);
      const errorMessage = error.response?.data?.error || 'Ошибка при запуске проверки';
      alert(errorMessage);
    } finally {
      setCheckingId(null);
    }
  };

  // Функция для отслеживания статуса проверки
  const pollCheckStatus = async (checkId) => {
    const maxAttempts = 60; // Максимум 60 попыток (2 минуты)
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await axios.get(`/api/check-history/${checkId}`);
        const check = response.data;

        if (check.status === 'completed' || check.status === 'error') {
          // Проверка завершена, показываем результат
          setCheckResult(check);
          setShowCheckResultModal(true);
          fetchRequests(); // Обновляем список запросов для обновления даты проверки
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          // Повторяем через 2 секунды
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error('Error polling check status:', error);
      }
    };

    // Начинаем отслеживание через 2 секунды
    setTimeout(checkStatus, 2000);
  };

  // Функции для работы с ИИ
  const handleAiCitySelect = (cityKey, cityName) => {
    setAiFormData({ ...aiFormData, city: cityKey });
    setSelectedAiCityName(cityName);
    setAiCitySearch(cityName);
    setShowAiCityDropdown(false);
  };

  const handleImportCitySelect = (cityKey, cityName) => {
    setImportFormData({ ...importFormData, city: cityKey });
    setSelectedImportCityName(cityName);
    setImportCitySearch(cityName);
    setShowImportCityDropdown(false);
  };

  const handleAiSearchEngineChange = (engine) => {
    const currentEngines = aiFormData.searchEngines;
    let newEngines;

    if (currentEngines.includes(engine)) {
      newEngines = currentEngines.filter(e => e !== engine);
      if (newEngines.length === 0) {
        return;
      }
    } else {
      newEngines = [...currentEngines, engine];
    }

    setAiFormData({ ...aiFormData, searchEngines: newEngines });
  };

  const handleGenerateRequests = async () => {
    if (!aiFormData.prompt.trim()) {
      alert('Введите промпт для генерации запросов');
      return;
    }

    if (!aiFormData.projectId) {
      alert('Выберите проект для генерации запросов');
      return;
    }

    setIsGenerating(true);

    try {
      const response = await axios.post('/api/generate-requests', {
        projectId: aiFormData.projectId,
        prompt: aiFormData.prompt
      });

      setGeneratedRequests(response.data.requests);
      setSelectedRequests(new Set()); // Сбрасываем выбор
    } catch (error) {
      console.error('Error generating requests:', error);
      const errorMessage = error.response?.data?.error || 'Ошибка при генерации запросов';
      alert(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRequestToggle = (index) => {
    const newSelected = new Set(selectedRequests);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRequests(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRequests.size === generatedRequests.length) {
      setSelectedRequests(new Set());
    } else {
      setSelectedRequests(new Set(generatedRequests.map((_, index) => index)));
    }
  };

  const handleSaveSelectedRequests = async () => {
    if (selectedRequests.size === 0) {
      alert('Выберите хотя бы один запрос для сохранения');
      return;
    }

    setIsSavingBulk(true);

    try {
      const selectedRequestTexts = Array.from(selectedRequests).map(index => generatedRequests[index]);
      const selectedCityName = citiesData[aiFormData.city];

      await axios.post('/api/requests/bulk', {
        requests: selectedRequestTexts,
        city: selectedCityName,
        searchEngines: aiFormData.searchEngines,
        projectId: aiFormData.projectId
      });

      // Обновляем списки запросов
      await Promise.all([
        fetchAllRequests(),
        fetchRequests()
      ]);

      // Закрываем модальное окно
      setShowAIModal(false);
      alert(`Успешно добавлено ${selectedRequestTexts.length * aiFormData.searchEngines.length} запросов`);
    } catch (error) {
      console.error('Error saving requests:', error);
      const errorMessage = error.response?.data?.error || 'Ошибка при сохранении запросов';
      alert(errorMessage);
    } finally {
      setIsSavingBulk(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Проверяем обязательные поля
    if (!formData.query.trim()) {
      alert('Поисковый запрос обязателен для заполнения');
      return;
    }

    if (!formData.projectId) {
      alert('Необходимо выбрать проект для запроса');
      return;
    }

    try {
      const selectedCityName = citiesData[formData.city];

      if (editingRequest) {
        // При редактировании обновляем только текущий запрос
        const requestData = {
          query: formData.query,
          region: selectedCityName,
          search_engine: editingRequest.search_engine,
          status: editingRequest.status,
          project_id: formData.projectId
        };
        await axios.put(`/api/requests/${editingRequest.id}`, requestData);
      } else {
        // При добавлении создаем запросы для выбранных поисковых систем
        const requests = formData.searchEngines.map(engine => ({
          query: formData.query,
          region: selectedCityName,
          search_engine: engine,
          status: 'active',
          project_id: formData.projectId
        }));

        // Создаем все выбранные запросы параллельно
        await Promise.all(
          requests.map(request => axios.post('/api/requests', request))
        );
      }

      setShowModal(false);
      setEditingRequest(null); // Сбрасываем состояние редактирования
      await Promise.all([
        fetchAllRequests(), // Обновляем все запросы для счетчиков
        fetchRequests() // Обновляем отфильтрованный список запросов
      ]);
    } catch (error) {
      console.error('Error saving request:', error);
      // Если это конфликт (дубликат), показываем сообщение от сервера
      if (error.response?.status === 409) {
        alert(error.response.data.message);
      } else {
        alert(`Ошибка при сохранении запроса: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const handleImportClick = () => {
    if (projects.length === 0) {
      alert('Сначала создайте проект для группировки запросов!');
      return;
    }

    let defaultProjectId;
    if (selectedProject === 'all') {
      defaultProjectId = projects[0]?.id;
    } else {
      defaultProjectId = selectedProject;
    }

    setImportFormData({
      projectId: defaultProjectId,
      city: 'moscow',
      searchEngines: ['google', 'yandex']
    });
    setImportCitySearch('Москва');
    setSelectedImportCityName('Москва');
    setImportFile(null);
    setShowImportModal(true);
  };

  const handleImportSearchEngineChange = (engine) => {
    const currentEngines = importFormData.searchEngines;
    let newEngines;
    if (currentEngines.includes(engine)) {
      newEngines = currentEngines.filter(e => e !== engine);
      if (newEngines.length === 0) return;
    } else {
      newEngines = [...currentEngines, engine];
    }
    setImportFormData({ ...importFormData, searchEngines: newEngines });
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) {
      alert('Выберите файл для импорта');
      return;
    }
    if (!importFormData.projectId) {
      alert('Выберите проект');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      // Split by newlines and filter empty strings
      const requests = text.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 0);

      if (requests.length === 0) {
        alert('Файл пуст или не содержит корректных строк');
        return;
      }

      setIsImporting(true);
      try {
        const selectedCityName = citiesData[importFormData.city];
        const response = await axios.post('/api/requests/bulk', {
          requests,
          city: selectedCityName,
          searchEngines: importFormData.searchEngines,
          projectId: importFormData.projectId
        });

        await Promise.all([
          fetchAllRequests(),
          fetchRequests()
        ]);

        setShowImportModal(false);
        alert(response.data.message);

      } catch (error) {
        console.error('Import error:', error);
        alert(`Ошибка импорта: ${error.response?.data?.error || error.message}`);
      } finally {
        setIsImporting(false);
      }
    };
    reader.readAsText(importFile);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Функция для изменения выбранного проекта с сохранением в localStorage
  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    localStorage.setItem('selectedProject', projectId);
  };

  return (
    <div className="requests">
      <div className="requests-header">
        <div className="header-left">
          <h1>Запросы</h1>
          {projects.length > 0 && (
            <div className="project-selector">
              <select
                value={selectedProject}
                onChange={(e) => handleProjectChange(e.target.value)}
                className="header-project-select"
              >
                <option value="all">Все проекты</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="requests-actions">
          <button
            className="btn btn-secondary"
            onClick={handleAddRequest}
            disabled={projects.length === 0}
          >
            <Plus size={16} />
            Добавить запрос
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleImportClick}
            disabled={projects.length === 0}
          >
            <Upload size={16} />
            Импорт
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddWithAI}
            disabled={projects.length === 0}
          >
            <Bot size={16} />
            Добавить через ИИ
          </button>
        </div>
      </div>

      <div className="requests-tabs">
        <button
          className={`tab ${activeTab === 'google' ? 'active' : ''}`}
          onClick={() => handleTabChange('google')}
        >
          Google ({getCountForEngine('google')})
        </button>
        <button
          className={`tab ${activeTab === 'yandex' ? 'active' : ''}`}
          onClick={() => handleTabChange('yandex')}
        >
          Яндекс ({getCountForEngine('yandex')})
        </button>
      </div>

      {projects.length > 0 && selectedProject !== 'all' && selectedProject !== 'unassigned' && (
        <div className="project-info">
          <span className="project-badge">
            Проект: {projects.find(p => p.id.toString() === selectedProject)?.title || 'Неизвестный проект'}
          </span>
        </div>
      )}



      <div className="requests-content">
        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : projects.length === 0 ? (
          <div className="empty-projects-message">
            <h3>Нет проектов</h3>
            <p>Для создания запросов сначала необходимо создать проект.</p>
            <button
              className="create-project-btn"
              onClick={() => window.location.href = '/dashboard/projects'}
            >
              Создать проект
            </button>
          </div>
        ) : (
          <div className="requests-table-container">
            {/* Панель массового удаления */}
            {getCurrentRequests.length > 0 && (
              <div className="bulk-actions-bar">
                <button
                  className="select-all-btn"
                  onClick={handleSelectAllForDelete}
                >
                  {selectedForDelete.size === allProjectRequests.length && allProjectRequests.length > 0 ? (
                    <><CheckSquare size={16} /> Снять выделение ({allProjectRequests.length})</>
                  ) : (
                    <><Square size={16} /> Выбрать все ({allProjectRequests.length})</>
                  )}
                </button>
                {selectedForDelete.size > 0 && (
                  <>
                    <button
                      className="bulk-check-btn"
                      onClick={handleBulkCheck}
                      disabled={isBulkChecking}
                    >
                      <Search size={16} />
                      {isBulkChecking ? 'Добавление...' : `Проверить выбранные (${selectedForDelete.size})`}
                    </button>
                    <button
                      className="bulk-delete-btn"
                      onClick={handleBulkDelete}
                    >
                      <Trash2 size={16} />
                      Удалить выбранные ({selectedForDelete.size})
                    </button>
                  </>
                )}
              </div>
            )}
            <table className="requests-table">
              <thead>
                <tr>
                  <th className="checkbox-cell">
                    <button
                      className="checkbox-btn"
                      onClick={handleSelectAllForDelete}
                      title={selectedForDelete.size === allProjectRequests.length && allProjectRequests.length > 0 ? `Снять выделение (${allProjectRequests.length})` : `Выбрать все (${allProjectRequests.length})`}
                    >
                      {selectedForDelete.size === allProjectRequests.length && allProjectRequests.length > 0 ? (
                        <CheckSquare size={18} />
                      ) : (
                        <Square size={18} />
                      )}
                    </button>
                  </th>
                  <th>№</th>
                  <th>Запрос</th>
                  <th>Проект</th>
                  <th>Регион</th>
                  <th>Последняя дата проверки</th>
                  <th>Вхождение</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {getCurrentRequests.map((request, index) => (
                  <tr key={request.id} className={selectedForDelete.has(request.id) ? 'selected' : ''}>
                    <td className="checkbox-cell">
                      <button
                        className="checkbox-btn"
                        onClick={() => handleToggleSelect(request.id)}
                      >
                        {selectedForDelete.has(request.id) ? (
                          <CheckSquare size={18} />
                        ) : (
                          <Square size={18} />
                        )}
                      </button>
                    </td>
                    <td data-label="№">{indexOfFirstItem + index + 1}</td>
                    <td className="query-cell" data-label="Запрос">{request.query}</td>
                    <td className="project-cell" data-label="Проект">
                      {request.project_title || '—'}
                    </td>
                    <td data-label="Регион">{request.region}</td>
                    <td data-label="Дата проверки">{request.last_check || '—'}</td>
                    <td className="brand-score-cell" data-label="Вхождение">
                      {request.brand_score === null || request.brand_score === undefined ? (
                        <span className="brand-score-dash">—</span>
                      ) : request.brand_score === 0 ? (
                        <span className="brand-score-zero">✗ Нет</span>
                      ) : (
                        <span className="brand-score high">✓ Да</span>
                      )}
                    </td>
                    <td className="actions-cell" data-label="">
                      <button
                        type="button"
                        className="action-btn check-btn"
                        style={{
                          width: 'auto',
                          whiteSpace: 'nowrap',
                          padding: '0 8px'
                        }}
                        onClick={() => handleCheck(request.id)}
                        disabled={checkingId === request.id}
                        title="Проверить позицию"
                      >
                        {checkingId === request.id ? 'Проверка...' : 'Проверить'}
                      </button>
                      <button
                        type="button"
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(request)}
                        title="Редактировать"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        type="button"
                        className="action-btn delete-btn"
                        onClick={(e) => handleDelete(request.id, e)}
                        disabled={deletingId === request.id}
                        title="Удалить"
                      >
                        {deletingId === request.id ? (
                          <span>...</span>
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {getCurrentRequests.length === 0 && !loading && (
              <div className="empty-state">
                <p>Запросы не найдены</p>
                <p>Добавьте первый запрос для отслеживания позиций</p>
              </div>
            )}

            {/* Пагинация */}
            {requests.length > 0 && (
              <div className="pagination">
                <div className="pagination-info">
                  <span>Показано {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, requests.length)} из {requests.length}</span>
                </div>
                <div className="pagination-controls">
                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    title="Первая страница"
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    title="Предыдущая страница"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="pagination-pages">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        return page === 1 ||
                          page === totalPages ||
                          Math.abs(page - currentPage) <= 1;
                      })
                      .reduce((acc, page, idx, arr) => {
                        if (idx > 0 && page - arr[idx - 1] > 1) {
                          acc.push('...');
                        }
                        acc.push(page);
                        return acc;
                      }, [])
                      .map((item, idx) => (
                        item === '...' ? (
                          <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>
                        ) : (
                          <button
                            key={item}
                            className={`btn-pagination-page ${currentPage === item ? 'active' : ''}`}
                            onClick={() => handlePageChange(item)}
                          >
                            {item}
                          </button>
                        )
                      ))
                    }
                  </div>

                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    title="Следующая страница"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage >= totalPages}
                    title="Последняя страница"
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>

                <div className="items-per-page">
                  <span>На странице:</span>
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="items-per-page-select"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Модальное окно для добавления/редактирования */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingRequest ? 'Редактировать запрос' : 'Добавить запрос'}</h2>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Поисковый запрос:</label>
                <input
                  type="text"
                  value={formData.query}
                  onChange={(e) => setFormData({ ...formData, query: e.target.value })}
                  placeholder="Введите поисковый запрос"
                  required
                />
              </div>

              <div className="form-group">
                <label>Город:</label>
                <div className="city-search-container">
                  <input
                    type="text"
                    value={citySearch}
                    onChange={(e) => {
                      setCitySearch(e.target.value);
                      setShowCityDropdown(true);
                    }}
                    onFocus={() => setShowCityDropdown(true)}
                    placeholder="Введите название города"
                    required
                  />
                  {showCityDropdown && filteredCities.length > 0 && (
                    <div className="city-dropdown">
                      {filteredCities.slice(0, 10).map((city) => (
                        <div
                          key={city.key}
                          className="city-option"
                          onClick={() => handleCitySelect(city.key, city.name)}
                        >
                          {city.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Поисковые системы:</label>
                <div className="search-engine-selection">
                  <button
                    type="button"
                    data-engine="google"
                    className={`search-engine-btn ${formData.searchEngines.includes('google') ? 'active' : ''}`}
                    onClick={() => handleSearchEngineChange('google')}
                    title={formData.searchEngines.includes('google') ? 'Google выбран' : 'Выбрать Google'}
                  >
                    Google
                  </button>
                  <button
                    type="button"
                    data-engine="yandex"
                    className={`search-engine-btn ${formData.searchEngines.includes('yandex') ? 'active' : ''}`}
                    onClick={() => handleSearchEngineChange('yandex')}
                    title={formData.searchEngines.includes('yandex') ? 'Яндекс выбран' : 'Выбрать Яндекс'}
                  >
                    Яндекс
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Проект: <span className="required">*</span></label>
                <select
                  value={formData.projectId || ''}
                  onChange={(e) => setFormData({ ...formData, projectId: e.target.value || null })}
                  className="form-select"
                  required
                >
                  <option value="">Выберите проект</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.title}
                    </option>
                  ))}
                </select>
                {projects.length === 0 && (
                  <p className="form-help error">Сначала создайте проект в разделе "Проекты"</p>
                )}
              </div>

              {!editingRequest && (
                <p className="form-help">
                  💡 Выберите поисковые системы для отслеживания позиций. Можно выбрать одну или обе системы одновременно.
                </p>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Отмена
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingRequest ? 'Сохранить' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно для добавления через ИИ */}
      {showAIModal && (
        <div className="modal-overlay">
          <div className="modal ai-modal">
            <div className="modal-header">
              <h2>Генерация запросов через ИИ</h2>
              <button
                className="modal-close"
                onClick={() => setShowAIModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-form ai-modal-form">
              <div className="form-group">
                <label>Проект:</label>
                <select
                  value={aiFormData.projectId || ''}
                  onChange={(e) => setAiFormData({ ...aiFormData, projectId: e.target.value })}
                  required
                >
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Промпт для ИИ:</label>
                <textarea
                  value={aiFormData.prompt}
                  onChange={(e) => setAiFormData({ ...aiFormData, prompt: e.target.value })}
                  placeholder="Опишите, какие запросы вы хотите получить. Например: 'Запросы для поиска веб-разработчиков в Москве', 'Поисковые фразы для студии маникюра', и т.д."
                  rows="4"
                  required
                />
              </div>

              <div className="form-group">
                <label>Город:</label>
                <div className="city-search-container">
                  <input
                    type="text"
                    value={aiCitySearch}
                    onChange={(e) => {
                      setAiCitySearch(e.target.value);
                      setShowAiCityDropdown(true);
                    }}
                    onFocus={() => setShowAiCityDropdown(true)}
                    placeholder="Введите название города"
                    required
                  />
                  {showAiCityDropdown && aiFilteredCities.length > 0 && (
                    <div className="city-dropdown">
                      {aiFilteredCities.slice(0, 10).map((city) => (
                        <div
                          key={city.key}
                          className="city-option"
                          onClick={() => handleAiCitySelect(city.key, city.name)}
                        >
                          {city.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Поисковые системы:</label>
                <div className="search-engine-selection">
                  <button
                    type="button"
                    className={`search-engine-btn ${aiFormData.searchEngines.includes('google') ? 'active' : ''}`}
                    onClick={() => handleAiSearchEngineChange('google')}
                  >
                    Google
                  </button>
                  <button
                    type="button"
                    className={`search-engine-btn ${aiFormData.searchEngines.includes('yandex') ? 'active' : ''}`}
                    onClick={() => handleAiSearchEngineChange('yandex')}
                  >
                    Yandex
                  </button>
                </div>
              </div>

              <div className="form-group">
                <button
                  type="button"
                  className="btn btn-primary generate-btn"
                  onClick={handleGenerateRequests}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Генерация...' : 'Сгенерировать запросы'}
                </button>
              </div>

              {generatedRequests.length > 0 && (
                <div className="generated-requests-section">
                  <div className="requests-header">
                    <h3>Сгенерированные запросы ({generatedRequests.length})</h3>
                    <button
                      type="button"
                      className="btn btn-link select-all-btn"
                      onClick={handleSelectAll}
                    >
                      {selectedRequests.size === generatedRequests.length ? 'Снять все' : 'Выбрать все'}
                    </button>
                  </div>

                  <div className="requests-list">
                    {generatedRequests.map((request, index) => (
                      <div key={index} className="request-item">
                        <label className="request-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedRequests.has(index)}
                            onChange={() => handleRequestToggle(index)}
                          />
                          <span className="request-text">{request}</span>
                        </label>
                      </div>
                    ))}
                  </div>

                  <div className="requests-actions">
                    <div className="selected-count">
                      Выбрано: {selectedRequests.size} из {generatedRequests.length}
                      {selectedRequests.size > 0 && (
                        <span className="total-requests">
                          (будет создано {selectedRequests.size * aiFormData.searchEngines.length} запросов)
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary save-requests-btn"
                      onClick={handleSaveSelectedRequests}
                      disabled={selectedRequests.size === 0 || isSavingBulk}
                    >
                      {isSavingBulk ? 'Сохранение...' : 'Добавить выбранные'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно результата проверки */}
      {showCheckResultModal && checkResult && (
        <div className="modal-overlay">
          <div className="modal check-result-modal" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>Результат проверки</h2>
              <button
                className="close-btn"
                onClick={() => setShowCheckResultModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="check-result-info">
                <p><strong>Запрос:</strong> {checkResult.query}</p>
                <p><strong>Регион:</strong> {checkResult.region}</p>
                <p><strong>Дата проверки:</strong> {new Date(checkResult.check_date).toLocaleString('ru-RU')}</p>
                <p><strong>Статус:</strong>
                  <span className={`status-badge ${checkResult.status}`}>
                    {checkResult.status === 'completed' ? 'Завершено' :
                      checkResult.status === 'error' ? 'Ошибка' :
                        checkResult.status === 'running' ? 'Выполняется' : 'В очереди'}
                  </span>
                </p>

                {checkResult.error_message && (
                  <div className="error-message">
                    <p><strong>Ошибка:</strong> {checkResult.error_message}</p>
                  </div>
                )}

                {checkResult.alice_response && (
                  <div className="alice-response">
                    <h3>Ответ Алисы:</h3>
                    <div
                      className="response-content"
                      style={{
                        maxHeight: '300px',
                        overflow: 'auto',
                        backgroundColor: '#f5f5f5',
                        padding: '15px',
                        borderRadius: '8px',
                        lineHeight: '1.8'
                      }}
                      dangerouslySetInnerHTML={{ __html: checkResult.alice_response }}
                    />
                  </div>
                )}

                {/* Источники (из нового формата serp_items.sources) */}
                {checkResult.serp_items?.sources && checkResult.serp_items.sources.length > 0 && (
                  <div className="sources-results">
                    <h3>Источники ({checkResult.serp_items.sources.length}):</h3>
                    <ul className="sources-list" style={{ listStyle: 'none', padding: 0 }}>
                      {checkResult.serp_items.sources.map((source, index) => (
                        <li key={index} style={{
                          marginBottom: '10px',
                          padding: '10px',
                          backgroundColor: source.used ? '#e6f7e6' : '#f9f9f9',
                          borderRadius: '4px',
                          borderLeft: source.used ? '3px solid #52c41a' : 'none'
                        }}>
                          <span className="position-badge" style={{
                            backgroundColor: source.used ? '#52c41a' : '#1890ff',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            marginRight: '10px'
                          }}>{index + 1}</span>
                          {source.used && <span style={{ color: '#52c41a', marginRight: '10px', fontWeight: 'bold' }}>[Использован]</span>}
                          <a href={source.url} target="_blank" rel="noopener noreferrer">{source.title || 'Без названия'}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* SERP результаты (из нового формата serp_items.serpItems или старый формат - массив) */}
                {(checkResult.serp_items?.serpItems?.length > 0 || (Array.isArray(checkResult.serp_items) && checkResult.serp_items.length > 0)) && (
                  <div className="serp-results">
                    <h3>Результаты поиска:</h3>
                    <ul className="serp-list" style={{ listStyle: 'none', padding: 0 }}>
                      {(checkResult.serp_items?.serpItems || checkResult.serp_items).map((item, index) => (
                        <li key={index} style={{
                          marginBottom: '10px',
                          padding: '10px',
                          backgroundColor: '#f9f9f9',
                          borderRadius: '4px'
                        }}>
                          <span className="position-badge" style={{
                            backgroundColor: '#1890ff',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            marginRight: '10px'
                          }}>{item.position || index + 1}</span>
                          <a href={item.url} target="_blank" rel="noopener noreferrer">{item.title}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Связанные запросы */}
                {checkResult.serp_items?.searchQueries && checkResult.serp_items.searchQueries.length > 0 && (
                  <div className="search-queries">
                    <h3>Связанные запросы ({checkResult.serp_items.searchQueries.length}):</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {checkResult.serp_items.searchQueries.map((query, index) => (
                        <li key={index} style={{
                          marginBottom: '5px',
                          padding: '8px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '4px'
                        }}>
                          <Search size={14} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                          {typeof query === 'string' ? query : query.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Подсказки */}
                {checkResult.serp_items?.hints && checkResult.serp_items.hints.length > 0 && (
                  <div className="hints">
                    <h3>Подсказки ({checkResult.serp_items.hints.length}):</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {checkResult.serp_items.hints.map((hint, index) => (
                        <li key={index} style={{
                          marginBottom: '5px',
                          padding: '8px',
                          backgroundColor: '#fff7e6',
                          borderRadius: '4px',
                          borderLeft: '3px solid #faad14'
                        }}>
                          {typeof hint === 'string' ? hint : (hint.text || hint.url || JSON.stringify(hint))}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowCheckResultModal(false)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно импорта */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Импорт запросов</h2>
              <button
                className="close-btn"
                onClick={() => setShowImportModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleImportSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Проект</label>
                  <select
                    value={importFormData.projectId || ''}
                    onChange={(e) => setImportFormData({ ...importFormData, projectId: e.target.value })}
                    required
                  >
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Город</label>
                  <div className="city-search-container">
                    <input
                      type="text"
                      value={importCitySearch}
                      onChange={(e) => {
                        setImportCitySearch(e.target.value);
                        setShowImportCityDropdown(true);
                      }}
                      onFocus={() => setShowImportCityDropdown(true)}
                      placeholder="Введите название города"
                      required
                    />
                    {showImportCityDropdown && (
                      <div className="city-dropdown">
                        {cities
                          .filter(city => city.name.toLowerCase().includes(importCitySearch.toLowerCase()))
                          .slice(0, 10).map((city) => (
                            <div
                              key={city.key}
                              className="city-option"
                              onClick={() => handleImportCitySelect(city.key, city.name)}
                            >
                              {city.name}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Поисковые системы</label>
                  <div className="search-engine-selection">
                    <button
                      type="button"
                      className={`search-engine-btn ${importFormData.searchEngines.includes('google') ? 'active' : ''}`}
                      onClick={() => handleImportSearchEngineChange('google')}
                    >
                      Google
                    </button>
                    <button
                      type="button"
                      className={`search-engine-btn ${importFormData.searchEngines.includes('yandex') ? 'active' : ''}`}
                      onClick={() => handleImportSearchEngineChange('yandex')}
                    >
                      Yandex
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Файл с запросами (.txt)</label>
                  <div className="file-upload-container">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={(e) => setImportFile(e.target.files[0])}
                      required
                      style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }}
                    />
                    <div className="help-text" style={{ fontSize: '0.85rem', color: '#666', marginTop: '5px' }}>
                      Формат: один запрос на строку
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportModal(false)}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isImporting || !importFile}
                >
                  {isImporting ? 'Импорт...' : 'Импортировать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal confirm-modal">
            <div className="modal-header">
              <h2>Подтверждение удаления</h2>
            </div>

            <div className="modal-body">
              <p>Вы уверены, что хотите удалить этот запрос?</p>
              <p className="warning-text">Это действие нельзя будет отменить.</p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelDelete}
                disabled={deletingId === showDeleteConfirm}
              >
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deletingId === showDeleteConfirm}
              >
                {deletingId === showDeleteConfirm ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения массового удаления */}
      {showBulkCheckConfirm && (
        <div className="modal-overlay">
          <div className="modal confirm-modal">
            <div className="modal-header">
              <h2>⚠️ Подтверждение массовой проверки</h2>
            </div>

            <div className="modal-body">
              <p>Вы собираетесь запустить проверку <strong>{selectedForDelete.size}</strong> запросов.</p>
              {selectedForDelete.size > 100 && (
                <div className="warning-banner" style={{
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginTop: '12px',
                  color: '#856404'
                }}>
                  <strong>⚠️ Внимание!</strong> Большое количество запросов ({selectedForDelete.size} шт.) может занять длительное время и создать высокую нагрузку. Рекомендуется проверять не более 100 запросов за раз.
                </div>
              )}
              {selectedProject === 'all' && (
                <div className="warning-banner" style={{
                  background: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginTop: '12px',
                  color: '#721c24'
                }}>
                  <strong>🚨 Вы выбрали "Все проекты"!</strong> Будут проверены запросы из всех проектов. Выберите конкретный проект, если нужно проверить только его запросы.
                </div>
              )}
              <p style={{ marginTop: '12px', color: '#666', fontSize: '0.9em' }}>
                Каждый запрос будет отправлен в очередь на проверку. Проверка выполняется в фоне.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelBulkCheck}
                disabled={isBulkChecking}
              >
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmBulkCheck}
                disabled={isBulkChecking}
              >
                {isBulkChecking ? 'Добавление...' : `Подтвердить (${selectedForDelete.size} шт.)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal confirm-modal">
            <div className="modal-header">
              <h2>Подтверждение удаления</h2>
            </div>

            <div className="modal-body">
              <p>Вы уверены, что хотите удалить выбранные запросы ({selectedForDelete.size} шт.)?</p>
              <p className="warning-text">Это действие нельзя будет отменить.</p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelBulkDelete}
                disabled={isBulkDeleting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Requests;
